export function extractHtmlFromElementorJson(json: any): string {
  // 1) Locate the array of element objects
  const parsed = typeof json === 'string' ? JSON.parse(json) : json;
  let arr: any[] | undefined = undefined;
  if (Array.isArray(parsed.content)) {
    arr = parsed.content;
  } else if (Array.isArray(parsed.elements)) {
    arr = parsed.elements;
  } else {
    // try first array property
    for (const key of Object.keys(parsed)) {
      const maybe = (parsed as any)[key];
      if (Array.isArray(maybe)) {
        arr = maybe;
        break;
      }
    }
  }

  if (!arr) {
    return '<p style="color:#ff0000;">Invalid Elementor JSON: no content/elements found.</p>';
  }

  function getVisibilityClasses(settings: any): string {
    const classes: string[] = [];
    if (settings.hide_mobile) {
      classes.push('hidden-mobile');
    }
    if (settings.hide_desktop) {
      classes.push('hidden-desktop');
    }
    const cssClasses = settings._css_classes || '';
    if (cssClasses) {
      classes.push(...cssClasses.split(' '));
    }
    return classes.join(' ');
  }

  function resolveCurrencySymbol(symbol: string): string {
    const normalized = (symbol || '').trim().toLowerCase();
    const map: Record<string, string> = {
      euro: '€',
      eur: '€',
      dollar: '$',
      usd: '$',
      gbp: '£',
      pound: '£',
      yen: '¥',
      jpy: '¥',
    };

    return map[normalized] || symbol || '';
  }

  function renderTabsWidget(tabs: any[], variant: 'toggle' | 'accordion', visibilityClasses: string): string {
    const widgetClass = `elementor-${variant}`;
    const itemClass = `elementor-${variant}-item`;
    const iconClass = `elementor-${variant}-icon`;

    const tabHtml = tabs
      .map(tab => {
        const tabTitle = tab.tab_title || '';
        const tabContent = tab.tab_content || '';
        const tabId = tab._id || 'tab';
        return `
          <div class="${itemClass}">
            <div id="elementor-tab-title-${tabId}" class="elementor-tab-title">
              <span class="elementor-tab-title-text">${tabTitle}</span>
              <span class="${iconClass}"><i class="fas fa-plus"></i></span>
            </div>
            <div id="elementor-tab-content-${tabId}" class="elementor-tab-content">${tabContent}</div>
          </div>`;
      })
      .join('');

    return `<div class="${widgetClass} ${visibilityClasses}">${tabHtml}</div>`;
  }

  // 2) Render each element
  function renderElement(el: any): string {
    const elementType = el.elType || '';
    const widgetType = el.widgetType;
    const settings = el.settings || {};
    const children = Array.isArray(el.elements)
      ? el.elements.map(renderElement).join('')
      : '';

    const visibility_classes = getVisibilityClasses(settings);

    if(elementType === 'section') {
      const bgcolor = settings.background_color || '#ffffff';
      const classList = `elementor-section ${visibility_classes}`.trim();
      return `<div class="${classList}" style="background-color:${bgcolor};">${children}</div>`;
    }
    else if(elementType === 'column') {
      const width = settings._column_size || '100';
      const classList = `elementor-column width-${width} ${visibility_classes}`.trim();
      return `<div class="${classList}">${children}</div>`;
    }
    else if(elementType === 'container') {
      const classList = `elementor-container ${visibility_classes}`.trim();
      return `<div class="${classList}">${children}</div>`;
    }

    switch (widgetType) {
      case 'html':
        const content = settings.html || '';
        return `<div class="elementor-html ${visibility_classes}">${content}</div>`;

      case 'heading': {
        const title = settings.title || '';
        const tag = settings.header_size || 'h2';
        const classList = `elementor-heading-title ${visibility_classes}`.trim();
        if(title) return `<${tag} class="${classList}">${title}</${tag}>`;
        return '';
      }

      case 'image': {
        const imageData = settings.image || {};
        const imageUrl = imageData.url || '';
        const altText = imageData.alt || '';

        const visibility_classes = [];
        if (settings.hide_desktop || settings.hide_desktop_legacy) {
          visibility_classes.push('hidden-desktop');
        }
        if (settings.hide_mobile || settings.hide_mobile_legacy) {
          visibility_classes.push('hidden-mobile');
        }
        if (settings.hide_tablet || settings.hide_tablet_legacy) {
          visibility_classes.push('hidden-tablet');
        }
        if (settings._css_classes) {
          visibility_classes.push(settings._css_classes);
        }

        const classList = `elementor-image-wrapper ${visibility_classes.join(' ')}`.trim();
        if (imageUrl) {
          return `<div class="${classList}">
            <img src="${imageUrl}" class="elementor-image" alt="${altText}"/>
          </div>`;
        }
        return '';
      }

      case 'image-box': {
        const titleText = settings.title_text || '';
        const descriptionText = settings.description_text || '';
        const imageData = settings.image || {};
        const imageUrl = imageData.url || '';
        const imagePosition = settings.position || 'top';

        return `
          <div class="elementor-image-box-wrapper elementor-image-position-${imagePosition} ${visibility_classes}">
            ${imageUrl ? `<figure class="elementor-image-box-img">
              <img src="${imageUrl}" alt="${titleText}"/>
            </figure>` : ''}
            <div class="elementor-image-box-content">
              ${titleText ? `<h3 class="elementor-image-box-title">${titleText}</h3>` : ''}
              ${descriptionText ? `<p class="elementor-image-box-description">${descriptionText}</p>` : ''}
            </div>
          </div>`;
      }

      case 'button': {
        const text = settings.text || '';
        if(text === '') return '';
        const linkData = settings.link || {};
        const link = typeof linkData === 'object' ? linkData.url || '#' : '#';
        const classList = `elementor-button ${visibility_classes}`.trim();

        const background = settings.background_color || '#0d6efd';
        const color = settings.text_color || '#ffffff';

        return `<a href="${link}" class="${classList}" style="background-color:${background};color:${color};text-decoration:none;padding:12px 24px;display:inline-block;border-radius:4px;font-weight:500;margin:10px 0;">${text}</a>`;
      }

      case 'text-editor':
        const contentTextEditor = settings.editor || '';
        const classList = `elementor-text-editor ${visibility_classes}`.trim();
        if(contentTextEditor) return `<div class="${classList}">${contentTextEditor}</div>`;
        return '';

      case 'icon-list': {
        const items: any[] = settings.icon_list || [];
        if (items.length > 0) {
          const listItems = items
            .map(item => {
              const selected_icon = item.selected_icon || {};
              const iconValue = selected_icon.value || 'fas fa-check-circle';
              const text = item.text || '';
              const iconHtml = `<i class="${iconValue}"></i>`;
              return `
                <li class="elementor-icon-list-item">
                  <span class="elementor-icon">${iconHtml}</span>
                  <span class="elementor-icon-list-text">${text}</span>
                </li>`;
            })
            .join('');
          const classList = `elementor-icon-list-items ${visibility_classes}`.trim();
          return `<ul class="${classList}">${listItems}</ul>`;
        }
        return '';
      }

      case 'icon-box': {
        const title = settings.title_text || '';
        const description = settings.description_text || '';
        return `
          <div class="elementor-icon-box-wrapper ${visibility_classes}">
            <div class="elementor-icon-box-icon">
              <span class="elementor-icon"><i class="fas fa-star"></i></span>
            </div>
            <div class="elementor-icon-box-content">
              <h3 class="elementor-icon-box-title">${title}</h3>
              <p class="elementor-icon-box-description">${description}</p>
            </div>
          </div>`;
      }

      case 'blockquote': {
        const content = settings.blockquote_content || '';
        const author = settings.author_name || '';
        return `
          <blockquote class="elementor-blockquote ${visibility_classes}">
            <p class="elementor-blockquote__content">${content}</p>
            <footer class="elementor-blockquote__author">${author}</footer>
          </blockquote>`;
      }

      case 'progress': {
        const content = settings.inner_text || '';
        const title = settings.title || '';
        return `
          <div class="progress">
            <strong>${title}</strong>
            <p>${content}</p>
          </div>`;
      }

      case 'testimonial': {
        const content = settings.testimonial_content || '';
        const name = settings.testimonial_name || '';
        const imgUrl = settings.image?.url || '';
        const job = settings.testimonial_job || '';
        
        return `
          <div class="elementor-testimonial-wrapper ${visibility_classes}">
            <div class="elementor-testimonial-content">${content}</div> 
            <div class="elementor-testimonial-meta">
              <div class="elementor-testimonial-meta-inner">
                <div class="elementor-testimonial-details">
                  ${imgUrl ? `<div class="elementor-testimonial-image"><img src="${imgUrl}" alt="${name}"/></div>` : ''}
                  <div class="elementor-testimonial-name">${name}</div>
                  <div class="elementor-testimonial-job">${job}</div>
                </div>
              </div>
            </div>
          </div>`;
      }

      case 'testimonial-carousel':
      case 'reviews': {
        const slides: any[] = settings.slides || [];
        const slideHtml = slides
          .map(slide => {
            const text = slide.content || '';
            const name = slide.name || '';
            const title = slide.title || '';
            return `
              <div class="elementor-testimonial-slide">
                <div class="elementor-testimonial-content">${text}</div>
                <div class="elementor-testimonial-meta">
                  <div class="elementor-testimonial-name">${name}</div>
                  <div class="elementor-testimonial-job">${title}</div>
                </div>
              </div>`;
          })
          .join('');
        const wrapperClass = widgetType === 'testimonial-carousel' ? 'elementor-testimonial-carousel-wrapper' : 'reviews-wrapper';
        return `<div class="${wrapperClass} ${visibility_classes}">
          <div class="elementor-testimonial-carousel">
            ${slideHtml}
          </div>
        </div>`;
      }

      case 'star-rating': {
        const title = settings.title || '';
        return `<div class="star-rating"><strong>${title}</strong></div>`;
      }

      case 'toggle': {
        const tabs: any[] = settings.tabs || [];
        return renderTabsWidget(tabs, 'toggle', visibility_classes);
      }

      case 'accordion': {
        const tabs: any[] = settings.tabs || [];
        return renderTabsWidget(tabs, 'accordion', visibility_classes);
      }

      case 'form': {
        const formFields: any[] = settings.form_fields || [];
        const fieldsHtml = formFields
          .map((field: any, index: number) => {
            const label = field.field_label || '';
            const placeholder = field.placeholder || '';
            const type = field.field_type || 'text';
            const required = field.required === 'true' ? 'required' : '';
            return `
              <label>
                ${label}
                <input type="${type}" name="field_${index}" placeholder="${placeholder}" ${required}/>
              </label>`;
          })
          .join('');
        const buttonText = settings.submit_button_text || 'Submit';
        return `<form>${fieldsHtml}<button type="submit">${buttonText}</button></form>`;
      }

      case 'price-table': {
        const heading = settings.heading || '';
        const subHeading = settings.sub_heading || '';
        const price = settings.price ?? '';
        const period = settings.period || '';
        const buttonText = settings.button_text || '';
        const additionalInfo = settings.footer_additional_info || '';
        const ribbonTitle = settings.ribbon_title || '';
        const link = settings.link?.url || '#';
        const features: any[] = settings.features_list || [];
        const currency = resolveCurrencySymbol(settings.currency_symbol || '');
        const originalPrice = settings.sale === 'yes' ? settings.original_price : null;
        const originalPriceHtml = originalPrice === null || originalPrice === undefined || originalPrice === ''
          ? ''
          : `<span class="elementor-price-table__original-price">${currency}${originalPrice}</span>`;

        const featureItems = features
          .map(item => {
            const itemText = item?.item_text || '';
            if (!itemText) return '';
            return `<li class="elementor-price-table__feature-item">${itemText}</li>`;
          })
          .join('');

        return `
          <div class="elementor-price-table ${visibility_classes}">
            ${ribbonTitle ? `<div class="elementor-price-table__ribbon">${ribbonTitle}</div>` : ''}
            ${heading ? `<div class="elementor-price-table__heading">${heading}</div>` : ''}
            ${subHeading ? `<div class="elementor-price-table__subheading">${subHeading}</div>` : ''}
            <div class="elementor-price-table__price-row">
              ${originalPriceHtml}
              <span class="elementor-price-table__price">${currency}${price}</span>
              ${period ? `<span class="elementor-price-table__period">${period}</span>` : ''}
            </div>
            ${featureItems ? `<ul class="elementor-price-table__features">${featureItems}</ul>` : ''}
            ${buttonText ? `<a href="${link}" class="elementor-price-table__button">${buttonText}</a>` : ''}
            ${additionalInfo ? `<div class="elementor-price-table__footer">${additionalInfo}</div>` : ''}
          </div>`;
      }

      case 'animated-headline': {
        const beforeText = settings.before_text || '';
        const highlighted = settings.highlighted_text || '';
        const rotating = settings.rotating_text || '';
        return `<h2>${beforeText} <span class="animated">${highlighted} ${rotating}</span></h2>`;
      }

      case 'alert': {
        const alertTitle = settings.alert_title || '';
        const alertDescription = settings.alert_description || '';
        const alertType = settings.alert_type || 'info';
        return `
        <div class="elementor-alert-${alertType}">
          <div class="elementor-alert ${visibility_classes}">
            <span class="elementor-alert-title">${alertTitle}</span>
            <span class="elementor-alert-description">${alertDescription}</span>
            <button class="elementor-alert-dismiss" aria-label="Dismiss this alert">
              <span aria-hidden="true">x</span>
            </button>
          </div>
        </div>`;
      }

      default:
        return children || '';
    }
  }

  const css = `
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css">
    <style>
      .previewDiv {
        font-family: Arial, sans-serif;
        line-height: 1.6;
        color: #333;
        background-color: #f7f7f7;
        container-type: inline-size;
      }
      .previewDiv.previewDeviceMobile {
        width: 100%;
        max-width: 460px;
        margin: 0 auto;
        border-radius: 8px;
        box-shadow: 0 12px 30px rgba(0,0,0,0.16);
        overflow: hidden;
        border: 1px solid #1f9e54;
      }
      .previewDiv * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      .previewDiv .elementor-section {
        margin-bottom: 30px;
        padding: 25px;
        background-color: red;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.05);
      }
      .previewDiv .elementor-column {
        padding: 15px;
        width: 100%;
      }
      .previewDiv .elementor-heading-title {
        color: #333;
        margin-bottom: 20px;
        font-weight: 600;
        line-height: 1.2;
      }
      .previewDiv .elementor-text-editor {
        margin-bottom: 20px;
        color: #666;
      }
      .previewDiv .elementor-image {
        width: 100%;
        margin: 20px 0;
      }
      .previewDiv .elementor-image img {
        width: 100%;
        height: auto;
        display: block;
        border-radius: 8px;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
      }
      .previewDiv .elementor-icon-list-items {
        list-style: none;
        padding: 0;
      }
      .previewDiv .elementor-icon-list-item {
        display: flex;
        align-items: center;
        margin-bottom: 15px;
        padding: 15px;
        background-color: #f8f9fa;
        border-radius: 6px;
      }
      .previewDiv .elementor-icon {
        margin-right: 15px;
        color: #FFD194;
      }
      .previewDiv .elementor-button {
        display: inline-block;
        padding: 12px 24px;
        text-decoration: none;
        border-radius: 4px;
        font-weight: 500;
        margin: 10px 0;
      }

      .previewDiv .elementor-price-table {
        position: relative;
        margin: 20px auto;
        padding: 20px;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        background-color: #fff;
        max-width: 560px;
        text-align: center;
      }
      .previewDiv .elementor-price-table__ribbon {
        position: absolute;
        top: 12px;
        right: 12px;
        background-color: #dc2626;
        color: #fff;
        font-size: 12px;
        font-weight: 700;
        padding: 4px 8px;
        border-radius: 999px;
      }
      .previewDiv .elementor-price-table__heading {
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 6px;
      }
      .previewDiv .elementor-price-table__subheading {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 12px;
        text-transform: uppercase;
      }
      .previewDiv .elementor-price-table__price-row {
        display: flex;
        align-items: baseline;
        justify-content: center;
        gap: 10px;
        margin-bottom: 16px;
      }
      .previewDiv .elementor-price-table__original-price {
        color: #6b7280;
        text-decoration: line-through;
        font-size: 18px;
      }
      .previewDiv .elementor-price-table__price {
        font-size: 42px;
        line-height: 1;
        font-weight: 700;
        color: #16a34a;
      }
      .previewDiv .elementor-price-table__period {
        color: #374151;
        font-size: 14px;
      }
      .previewDiv .elementor-price-table__features {
        list-style: none;
        margin: 0 0 16px 0;
        padding: 0;
      }
      .previewDiv .elementor-price-table__feature-item {
        margin-bottom: 8px;
      }
      .previewDiv .elementor-price-table__button {
        display: inline-block;
        padding: 10px 18px;
        border-radius: 999px;
        background-color: #16a34a;
        color: #fff;
        text-decoration: none;
        font-weight: 700;
      }
      .previewDiv .elementor-price-table__footer {
        margin-top: 12px;
        color: #374151;
        font-size: 14px;
        font-weight: 600;
      }

      /* Animated Headline */
      .previewDiv .elementor-headline {
        font-size: 24px;
        margin-bottom: 20px;
        font-weight: 600;
      }
      .previewDiv .elementor-headline-dynamic-wrapper {
        display: inline-block;
        position: relative;
      }
      .previewDiv .elementor-headline-dynamic-text {
        display: inline-block;
        color: #0d6efd;
      }
      .previewDiv .elementor-headline-rotating-item {
        display: none;
      }
      .previewDiv .elementor-headline-rotating-item:first-child {
        display: inline-block;
        color: #0d6efd;
      }

      /* Testimonial */
      .previewDiv .elementor-testimonial-wrapper {
        text-align: center;
        margin: 30px 0;
        padding: 20px;
        background-color: #f8f9fa;
        border-radius: 8px;
      }
      .previewDiv .elementor-testimonial-content {
        font-style: italic;
        margin-bottom: 15px;
        font-size: 18px;
      }
      .previewDiv .elementor-testimonial-meta {
        display: flex;
        justify-content: center;
      }
      .previewDiv .elementor-testimonial-name {
        font-weight: bold;
        margin-right: 5px;
      }

      /* Testimonial Carousel & Reviews */
      .previewDiv .elementor-testimonial-carousel-wrapper {
        margin: 30px 0;
      }
      .previewDiv .elementor-testimonial-slide {
        padding: 20px;
        background-color: #f8f9fa;
        border-radius: 8px;
        margin-bottom: 15px;
      }

      /* Blockquote */
      .previewDiv .elementor-blockquote {
        margin: 30px 0;
        padding: 20px;
        border-left: 5px solid #0d6efd;
        background-color: #f8f9fa;
      }
      .previewDiv .elementor-blockquote__content {
        font-style: italic;
        margin-bottom: 10px;
        font-size: 18px;
      }
      .previewDiv .elementor-blockquote__author {
        font-weight: bold;
      }

      /* Image Box */
      .previewDiv .elementor-image-box-wrapper {
        margin: 20px 0;
        text-align: center;
      }
      .previewDiv .elementor-image-box-title {
        margin-bottom: 10px;
        font-weight: 600;
      }

      /* Toggle */
      .previewDiv .elementor-toggle {
        margin: 20px 0;
      }
      .previewDiv .elementor-accordion {
        margin: 20px 0;
      }
      .previewDiv .elementor-toggle-item {
        border: 1px solid #dee2e6;
        border-radius: 4px;
        margin-bottom: 10px;
      }
      .previewDiv .elementor-accordion-item {
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        margin-bottom: 12px;
        overflow: hidden;
        background-color: #ffffff;
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
      }
      .previewDiv .elementor-tab-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 15px;
        font-weight: 600;
        background-color: #f8f9fa;
        border-bottom: 1px solid #dee2e6;
      }
      .previewDiv .elementor-tab-title-text {
        flex: 1;
      }
      .previewDiv .elementor-tab-content {
        padding: 15px;
      }
      .previewDiv .elementor-toggle-icon {
        margin-right: 10px;
      }
      .previewDiv .elementor-accordion-icon {
        color: #6b7280;
        font-size: 12px;
      }
      .previewDiv .elementor-accordion .elementor-tab-title {
        background-color: #ffffff;
      }

      /* Icon Box */
      .previewDiv .elementor-icon-box-wrapper {
        display: flex;
        margin: 20px 0;
        padding: 15px;
        background-color: #f8f9fa;
        border-radius: 8px;
      }
      .previewDiv .elementor-icon-box-icon {
        margin-right: 15px;
        font-size: 24px;
        color: #0d6efd;
      }
      .previewDiv .elementor-icon-box-title {
        margin-bottom: 10px;
        font-weight: 600;
      }

      .previewDiv .elementor-alert {
          border-inline-start:5px solid transparent;padding: 15px;
          position: relative;
          text-align: start
      }

      .previewDiv .elementor-alert .elementor-alert-title {
          display: block;
          font-weight: 700
      }

      .previewDiv .elementor-alert .elementor-alert-description {
          font-size: 13px
      }

      .previewDiv .elementor-alert button.elementor-alert-dismiss {
          background: transparent;
          border: none;
          color: inherit;
          cursor: pointer;
          font-size: 20px;
          inset-block-start: 10px;
          inset-inline-end: 10px;
          line-height: 1;
          padding: 3px;
          position: absolute;
      }

      .previewDiv .elementor-alert-info .elementor-alert {
          background-color: #d9edf7;
          border-color: #bcdff1;
          color: #31708f
      }

      .previewDiv .elementor-alert-success .elementor-alert {
          background-color: #dff0d8;
          border-color: #cae6be;
          color: #3c763d
      }

      .previewDiv .elementor-alert-warning .elementor-alert {
          background-color: #fcf8e3;
          border-color: #f9f0c3;
          color: #8a6d3b
      }

      .previewDiv .elementor-alert-danger .elementor-alert {
          background-color: #f2dede;
          border-color: #e8c4c4;
          color: #a94442
      }

      /* Responsive visibility */
      @container (width < 767px) {
        .previewDiv .hidden-mobile,
        .previewDiv .hidden-phone,
        .previewDiv .visible-desktop,
        .previewDiv .hidden-xs,
        .previewDiv .hidden-sm,
        .previewDiv .d-none.d-md-block,
        .previewDiv .elementor-hidden-mobile {
          display: none !important;
        }
        .previewDiv .elementor-column {
          width: 100% !important;
          float: none;
        }
      }

      @container (width >= 768px) {
        .previewDiv .hidden-desktop,
        .previewDiv .visible-mobile,
        .previewDiv .visible-phone,
        .previewDiv .d-md-none,
        .previewDiv .elementor-hidden-desktop,
        .previewDiv .d-none.d-sm-block.d-md-none {
          display: none !important;
        }
        .previewDiv .elementor-section {
          display: flex;
          flex-wrap: wrap;
        }
        .previewDiv .elementor-column {
          flex: 1;
        }
      }
    </style>
  `;

  const resultHtml = arr.map(renderElement).join('');
  return css + (resultHtml || '<p>No renderable content found in Elementor template.</p>');
}
